import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation35({ animation, badge, all_animations }: Props) {
  const [bubbles, setBubbles] = useState<number[]>([])
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    // Ajouter des bulles progressivement
    const bubbleInterval = setInterval(() => {
      setBubbles(prev => [...prev, prev.length])
    }, 150)

    setTimeout(() => {
      clearInterval(bubbleInterval)
      setShowBadge(true)
    }, 2000)

    return () => clearInterval(bubbleInterval)
  }, [])

  const bubbleData = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: -100 + Math.random() * 200,
    size: 20 + Math.random() * 40,
    delay: Math.random() * 0.5,
    duration: 1.5 + Math.random() * 1
  }))

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes bubbleRise {
          0% {
            transform: translateY(200px) translateX(var(--tx)) scale(0);
            opacity: 0;
          }
          20% {
            opacity: 0.8;
            transform: translateY(150px) translateX(calc(var(--tx) + 20px)) scale(1);
          }
          40% {
            transform: translateY(100px) translateX(calc(var(--tx) - 10px)) scale(1);
          }
          60% {
            transform: translateY(50px) translateX(calc(var(--tx) + 15px)) scale(1);
          }
          80% {
            transform: translateY(0px) translateX(calc(var(--tx) - 5px)) scale(1);
            opacity: 0.8;
          }
          100% {
            transform: translateY(-50px) translateX(var(--tx)) scale(0.5);
            opacity: 0;
          }
        }
        @keyframes popIn {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .bubble {
          animation: bubbleRise var(--duration) ease-out forwards;
        }
        .pop-in {
          animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .float {
          animation: float 2s ease-in-out infinite;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        <div className="relative">
          {/* Bulles qui montent */}
          {bubbleData.map(bubble => (
            bubbles.includes(bubble.id) && (
              <div
                key={bubble.id}
                className="absolute left-1/2 top-1/2 rounded-full border-2 border-tessan-yellow/50 bg-tessan-yellow/10 bubble"
                style={{
                  '--tx': `${bubble.x}px`,
                  '--duration': `${bubble.duration}s`,
                  width: `${bubble.size}px`,
                  height: `${bubble.size}px`,
                  marginLeft: `-${bubble.size / 2}px`,
                  animationDelay: `${bubble.delay}s`
                } as React.CSSProperties}
              >
                {/* Reflet de bulle */}
                <div className="absolute top-1 left-1 w-2 h-2 rounded-full bg-tessan-cream/50" />
              </div>
            )
          ))}

          {/* Badge */}
          <div className={`relative z-10 ${showBadge ? 'pop-in float' : 'opacity-0'}`}>
            <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
              <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
            </div>
          </div>
        </div>

        <div className={`mt-8 text-center transition-all duration-700 ${showBadge ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
